import { Bot, BriefcaseBusiness, FileText, Home, Menu, Moon, Network, Radar, Sparkles, Sun, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, NavLink, Route, Routes, useLocation } from "react-router-dom";
import { getPublicConfig } from "./api";
import { AskInterface } from "./components/AskInterface";
import { CaseStudiesPage, ExperienceV2Page } from "./components/ExperiencePages";
import { FloatingAssistant } from "./components/FloatingAssistant";
import { PrivacyPage, TermsPage } from "./components/LegalPages";
import { SchedulePage } from "./components/SchedulePage";
import { ArchitecturePage, ContactPage, FootprintPage, HomePage, InsightsPage, ResumePage } from "./components/Views";
import { documentTitleForPath } from "../shared/document-title";
import { validAssistantDeepLinkPrompt } from "../shared/assistant-deeplink";
import { useTheme } from "./theme";

const nav = [
  { to: "/", label: "Home", icon: Home },
  { to: "/insights", label: "Insights", icon: Sparkles },
  { to: "/experience", label: "Experience", icon: BriefcaseBusiness },
  { to: "/footprint", label: "Footprint", icon: Radar },
  { to: "/ai-assistant", label: "AI Assistant", icon: Bot },
  { to: "/resume", label: "Resume", icon: FileText },
];

export function App() {
  const [theme, setTheme] = useTheme();
  const [mobileNav, setMobileNav] = useState(false);
  const [siteKey, setSiteKey] = useState(import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "1x00000000000000000000AA");
  const [modelLabel, setModelLabel] = useState("Qwen3 30B · Cloudflare Workers AI");
  const [visitorCountryCode, setVisitorCountryCode] = useState("GB");
  const [componentAiEnabled, setComponentAiEnabled] = useState(false);
  const location = useLocation();
  useEffect(() => { setMobileNav(false); }, [location.pathname]);
  useEffect(() => { document.title = documentTitleForPath(location.pathname); }, [location.pathname]);
  useEffect(() => {
    void getPublicConfig().then((config) => {
      setSiteKey(config.turnstileSiteKey);
      setModelLabel(config.modelLabel);
      setVisitorCountryCode(config.visitorCountryCode || "GB");
      setComponentAiEnabled(config.componentAiEnabled);
    }).catch(() => undefined);
  }, []);
  const isLegal = location.pathname === "/terms" || location.pathname === "/privacy";
  const isSingleViewportRoute = ["/", "/ai-assistant", "/insights"].includes(location.pathname);
  const assistantDeepLinkPrompt = location.pathname === "/ai-assistant" ? validAssistantDeepLinkPrompt(new URLSearchParams(location.search).get("q")) ?? undefined : undefined;

  return (
    <div className={`app-shell ${isLegal ? "legal-shell" : ""} ${isSingleViewportRoute ? "single-viewport-shell" : ""}`}>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <header className="topbar">
        <div className="topbar-inner">
          <Link className="brand" to="/" aria-label="Omer Yoachimik home">
            <span className="brand-orb"><Sparkles size={16} /></span>
            <span>Omer Yoachimik</span>
          </Link>
          <nav id="primary-navigation" className={`primary-nav ${mobileNav ? "open" : ""}`} aria-label="Primary navigation">
            {nav.map(({ to, label, icon: Icon }) => <NavLink key={to} to={to}><Icon size={15} /><span>{label}</span></NavLink>)}
          </nav>
          <div className="topbar-actions">
            <button type="button" className="icon-button" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} mode`}>{theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}</button>
            <Link className="contact-pill" to="/contact">Contact Omer</Link>
            <button type="button" className="icon-button menu-button" onClick={() => setMobileNav(!mobileNav)} aria-label="Toggle navigation" aria-controls="primary-navigation" aria-expanded={mobileNav}>{mobileNav ? <X size={19} /> : <Menu size={19} />}</button>
          </div>
        </div>
      </header>

      <main id="main-content" className="main-content">
        <Routes>
          <Route path="/" element={<HomePage componentAiEnabled={componentAiEnabled} />} />
          <Route path="/ai-assistant" element={<AskInterface siteKey={siteKey} modelLabel={modelLabel} theme={theme} componentAiEnabled={componentAiEnabled} initialPrompt={assistantDeepLinkPrompt} clearInitialPromptFromUrl={Boolean(assistantDeepLinkPrompt)} />} />
          <Route path="/resume" element={<ResumePage componentAiEnabled={componentAiEnabled} />} />
          <Route path="/insights" element={<InsightsPage componentAiEnabled={componentAiEnabled} />} />
          <Route path="/experience" element={<ExperienceV2Page />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/footprint" element={<FootprintPage componentAiEnabled={componentAiEnabled} />} />
          <Route path="/architecture" element={<ArchitecturePage />} />
          <Route path="/contact" element={<ContactPage siteKey={siteKey} theme={theme} visitorCountryCode={visitorCountryCode} />} />
          <Route path="/schedule" element={<SchedulePage siteKey={siteKey} theme={theme} />} />
          <Route path="/schedule/" element={<SchedulePage siteKey={siteKey} theme={theme} />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="*" element={<section className="not-found"><h1>Page not found</h1><p>That page is not part of Omer's portfolio.</p><Link className="primary-action" to="/">Return home</Link></section>} />
        </Routes>
      </main>

      <FloatingAssistant siteKey={siteKey} modelLabel={modelLabel} theme={theme} componentAiEnabled={componentAiEnabled} />

      <footer className="site-footer">
        <span>© 2026 Omer Yoachimik</span>
        <nav aria-label="Footer navigation"><Link to="/terms"><FileText size={13} /> Terms</Link><Link to="/privacy">Privacy</Link><Link to="/architecture"><Network size={13} /> Architecture</Link></nav>
        <span>Experimental AI bio · London</span>
      </footer>
    </div>
  );
}
